import { useState, useEffect, useCallback } from "react";
import { Rocket } from "lucide-react";
import { api } from "@/services/api";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState } from "@/components/States";
import { NextLaunchSection } from "@/components/sections/NextLaunchSection";
import { UpcomingLaunchesSection } from "@/components/sections/UpcomingLaunchesSection";
import { SpaceNewsSection } from "@/components/sections/SpaceNewsSection";
import { QuickStatsRow } from "@/components/sections/QuickStatsRow";

export default function HomePage({ zipCode }) {
  const [launches, setLaunches] = useState(null);
  const [weather, setWeather] = useState(null);
  const [moon, setMoon] = useState(null);
  const [iss, setIss] = useState(null);
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [launchRes, weatherRes, moonRes, issRes, newsRes] = await Promise.all([
        api.getLaunches(5),
        api.getWeather(zipCode),
        api.getMoon(zipCode),
        api.getISS(),
        api.getNews(4),
      ]);
      setLaunches(launchRes.launches);
      setWeather(weatherRes);
      setMoon(moonRes);
      setIss(issRes);
      setNews(newsRes.news);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [zipCode]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const nextLaunch = launches?.[0];
  const upcomingLaunches = launches?.slice(1, 4);

  if (error && !launches) {
    return (
      <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        <ErrorState message={error} onRetry={fetchData} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        <PageHeader 
          icon={Rocket}
          title="Florida Launch Tracker"
          subtitle="Real-time rocket launches from Kennedy Space Center & Cape Canaveral"
        />

        <div className="space-y-6 md:space-y-8">
          <NextLaunchSection loading={loading} launch={nextLaunch} />
          <QuickStatsRow loading={loading} weather={weather} moon={moon} iss={iss} />
          <UpcomingLaunchesSection loading={loading} launches={upcomingLaunches} />
          <SpaceNewsSection loading={loading} news={news} />
        </div>

        <Footer />
      </div>
    </div>
  );
}
